export const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

export const revokePreviewIfBlob = (preview) => {
  if (typeof preview === 'string' && preview.startsWith('blob:')) {
    try {
      URL.revokeObjectURL(preview);
    } catch {
      /* ignore */
    }
  }
};

/**
 * Compress image using Canvas API.
 * Resizes to max 2000px width and converts to JPEG for smaller file size.
 */
export const compressImage = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        const MAX_WIDTH = 2000;
        if (width > MAX_WIDTH) {
          height = Math.round((height * MAX_WIDTH) / width);
          width = MAX_WIDTH;
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob.size > 500000) {
              canvas.toBlob(
                (reducedBlob) => {
                  const compressedFile = new File(
                    [reducedBlob],
                    file.name.replace(/\.[^.]+$/, '.jpg'),
                    { type: 'image/jpeg' },
                  );
                  resolve(compressedFile);
                },
                'image/jpeg',
                0.7,
              );
            } else {
              const compressedFile = new File(
                [blob],
                file.name.replace(/\.[^.]+$/, '.jpg'),
                { type: 'image/jpeg' },
              );
              resolve(compressedFile);
            }
          },
          'image/jpeg',
          0.85,
        );
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target.result;
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

/** Uploaded row: new uploads use `file` + `preview`; API rows use `path` and no `file`. */
export const getImageDisplaySrc = (img) => img?.preview || img?.path || '';

/** Submaster ids from JSON may be number or string; keep comparisons consistent. */
export const sameSubmasterId = (a, b) =>
  a !== undefined && b !== undefined && String(a) === String(b);

export const emptyPackRow = () => ({
  quantityPack: '',
  length: '',
  breadth: '',
  height: '',
  netWt: '',
});

/** Maps API/legacy rows: supports `quantityPack` or former `unit` key. */
export const normalizePackRow = (row) => ({
  quantityPack: row?.quantityPack ?? row?.quantity_pack ?? row?.unit ?? '',
  length: row?.length ?? '',
  breadth: row?.breadth ?? '',
  height: row?.height ?? '',
  netWt: row?.netWt ?? '',
});

export const normalizePackRows = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return [emptyPackRow()];
  return rows.map(normalizePackRow);
};

export const INITIAL_FORM = {
  uid: '',
  itemNo: '',
  category: '',
  productName: '',
  collection: [],
  materials: [],
  hsCode: '',
  color: [],
  finish: [],
  suitableFor: [],
  packType: [],
  uomCount: '',
  lengthDimension: '',
  breadthDimension: '',
  heightDimension: '',
  dimensionUnit: 'cm',
  netWeight: '',
  weightUnit: 'KG',
  moq: '',
  cost: '',
  remark: '',
  innerPacks: [emptyPackRow()],
  masterPacks: [emptyPackRow()],
};

/** API uses camelCase *Id fields; selects need numeric ids, not display names. */
export const pickSubmasterId = (product, camelKey, snakeKey) => {
  const v = product?.[camelKey] ?? product?.[snakeKey] ?? '';
  return v === '' || v === null || v === undefined ? '' : String(v);
};

export const idListFromRelation = (rel) => {
  if (!Array.isArray(rel)) return [];
  return rel
    .map((x) => (typeof x === 'object' && x != null ? x.id : x))
    .filter((id) => id !== undefined && id !== null && id !== '')
    .map((id) => String(id));
};

/**
 * Map API product payload into form state (spread over INITIAL_FORM).
 */
export function mapApiProductToForm(editingProduct) {
  return {
    ...INITIAL_FORM,
    uid: editingProduct.uid || '',
    itemNo: editingProduct.item_no || editingProduct.itemNo || '',
    category: pickSubmasterId(editingProduct, 'categoryId', 'category_id'),
    productName: pickSubmasterId(editingProduct, 'productNameId', 'product_name_id'),
    collection: idListFromRelation(editingProduct.collection),
    materials: idListFromRelation(editingProduct.materials),
    hsCode: pickSubmasterId(editingProduct, 'hsCodeId', 'hs_code_id'),
    uomCount: pickSubmasterId(editingProduct, 'uomCountId', 'uom_count_id'),
    color: idListFromRelation(editingProduct.color),
    finish: idListFromRelation(editingProduct.finish),
    suitableFor: idListFromRelation(editingProduct.suitableFor),
    packType: idListFromRelation(editingProduct.packType ?? editingProduct.pack_type),
    lengthDimension:
      editingProduct.lengthDimension != null && editingProduct.lengthDimension !== ''
        ? String(editingProduct.lengthDimension)
        : editingProduct.length_dimension != null && editingProduct.length_dimension !== ''
          ? String(editingProduct.length_dimension)
          : '',
    breadthDimension:
      editingProduct.breadthDimension != null && editingProduct.breadthDimension !== ''
        ? String(editingProduct.breadthDimension)
        : editingProduct.breadth_dimension != null && editingProduct.breadth_dimension !== ''
          ? String(editingProduct.breadth_dimension)
          : '',
    heightDimension:
      editingProduct.heightDimension != null && editingProduct.heightDimension !== ''
        ? String(editingProduct.heightDimension)
        : editingProduct.height_dimension != null && editingProduct.height_dimension !== ''
          ? String(editingProduct.height_dimension)
          : '',
    dimensionUnit: editingProduct.dimensionUnit || editingProduct.dimension_unit || 'cm',
    netWeight:
      editingProduct.netWeight != null && editingProduct.netWeight !== ''
        ? String(editingProduct.netWeight)
        : editingProduct.net_weight != null && editingProduct.net_weight !== ''
          ? String(editingProduct.net_weight)
          : '',
    weightUnit: editingProduct.weightUnit || editingProduct.weight_unit || 'KG',
    moq: editingProduct.moq != null && editingProduct.moq !== '' ? String(editingProduct.moq) : '',
    cost: editingProduct.cost != null && editingProduct.cost !== '' ? String(editingProduct.cost) : '',
    remark: editingProduct.remark || '',
    innerPacks: normalizePackRows(editingProduct.innerPacks ?? editingProduct.inner_packs),
    masterPacks: normalizePackRows(editingProduct.masterPacks ?? editingProduct.master_packs),
  };
}

export function validateProductForm(formData, uploadedImages) {
  const errs = {};

  if (!uploadedImages || uploadedImages.length === 0) {
    errs.images = 'At least one product image is required';
  }

  if (!formData.itemNo || !formData.itemNo.trim()) errs.itemNo = 'Item No. is required';
  if (!formData.category) errs.category = 'Category is required';
  if (!formData.productName) errs.productName = 'Product Name is required';
  if (!formData.hsCode) errs.hsCode = 'HS Code is required';

  if (!formData.materials || formData.materials.length === 0) {
    errs.materials = 'At least one Material is required';
  }
  if (!formData.collection || formData.collection.length === 0) {
    errs.collection = 'At least one Collection is required';
  }
  if (!formData.color || formData.color.length === 0) errs.color = 'At least one Color is required';
  if (!formData.finish || formData.finish.length === 0) errs.finish = 'At least one Finish is required';
  if (!formData.suitableFor || formData.suitableFor.length === 0) {
    errs.suitableFor = 'At least one Suitable For is required';
  }
  if (!formData.uomCount) errs.uomCount = 'UOM - Count is required';

  if (!formData.lengthDimension) errs.lengthDimension = 'Length is required';
  if (!formData.breadthDimension) errs.breadthDimension = 'Breadth is required';
  if (!formData.heightDimension) errs.heightDimension = 'Height is required';
  if (!formData.netWeight) errs.netWeight = 'Net Weight is required';

  if (!formData.moq || isNaN(formData.moq)) errs.moq = 'Valid MOQ is required';
  if (!formData.cost || isNaN(formData.cost)) errs.cost = 'Valid Cost is required';

  return errs;
}

/** Next UID from API (ACB######) or random fallback. */
export async function fetchNextProductUid() {
  try {
    const response = await fetch(`${process.env.REACT_BACKEND_BASE_URL}/api/v1/products/last-uid`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`,
      },
    });

    if (response.ok) {
      const data = await response.json();
      const lastNum = parseInt(data.uid.replace('ACB', '').slice(0, 6), 10);
      const next = lastNum + 1;
      const padded = String(next).padStart(6, '0');
      return `ACB${padded}`;
    }
    throw new Error('Could not fetch last UID from server');
  } catch (e) {
    console.warn('Using fallback UID generation:', e);
    const randomNum = Math.floor(Math.random() * 900000) + 100000;
    return `ACB${randomNum}`;
  }
}
